import { useEffect, useState } from "react";

import {
  Button,
  InfoModal,
  useModal,
  Table,
  RadioButton,
  Badge,
} from "@newsela/angelou";

import {
  setExperimentOverride,
  resetExperimentOverrides,
  getAllExperimentOverrides,
} from "@/experimentManager/utils";
import fetchJson from "@/remoteState/fetchJson";
import useUserExperimentSegments from "@/users/useUserExperimentSegments";
import useCanViewExperimentManager from "../utils/useCanSeeExperimentManager";
import {
  tableCss,
  modalStyles,
  modalFooterStyles,
  modalFooterUploadButtonStyles,
  modalFooterCancelButtonStyles,
} from "./ExperimentManager.style";

interface Experiments {
  [key: string]: {
    segments: string[];
    is_active: boolean;
  };
}

interface Experiment {
  [key: string]: string;
}

const ExperimentManager = () => {
  const [allExperiments, setAllExperiments] = useState<Experiments>({});
  const [modalProps, setActive, triggerRef] = useModal(false);
  const [experimentOverrides, setExperimentOverridesState] =
    useState<Experiment>({});
  const canViewManager = useCanViewExperimentManager();

  useEffect(() => {
    // @ts-expect-error void
    const fetcher = async (): void => {
      try {
        const result = await fetchJson("/api/v3/segmentation/experiment-list/");
        if (result) setAllExperiments(result as Experiments);
        const experimentOverrides = getAllExperimentOverrides();
        if (experimentOverrides)
          setExperimentOverridesState(experimentOverrides);
      } catch (_e) {
        // Nothing we can do here.
      }
    };
    fetcher();
  }, []);
  const { data: userExperiments, loading: loadingUserAllocations } =
    useUserExperimentSegments() as { data: Experiment; loading: boolean };

  if (!canViewManager) {
    return null;
  }
  const tableData =
    loadingUserAllocations || !allExperiments
      ? []
      : Object.keys(userExperiments || []).map((experimentKey) => {
          const segments = allExperiments[experimentKey]?.segments;
          const experimentSegments = (segments || []).map((segment: string) => {
            const isServerAllocated =
              userExperiments[experimentKey] === segment;
            const isChecked = experimentOverrides[experimentKey]
              ? experimentOverrides[experimentKey] === segment
              : userExperiments[experimentKey] === segment;
            const label = (
              <span>
                {isServerAllocated && (
                  <Badge
                    text="Original"
                    size={Badge.size.md}
                    flavor={Badge.flavor.solid}
                    color={Badge.color.blue}
                  />
                )}{" "}
                {isChecked ? <strong>{segment}</strong> : segment}
              </span>
            );
            return (
              <RadioButton
                key={experimentKey + segment}
                value={experimentKey + segment}
                name={experimentKey + segment}
                label={label as unknown as string}
                onChange={() => {
                  setExperimentOverride(experimentKey, segment);
                  setExperimentOverridesState({
                    ...experimentOverrides,
                    [experimentKey]: segment,
                  });
                }}
                checked={isChecked}
              />
            );
          });
          return {
            experimentName: <strong>{experimentKey}</strong>,
            segments: <div>{experimentSegments}</div>,
          };
        });
  const columns = [
    {
      accessor: "experimentName",
      Header: "Experiment Name",
    },
    {
      accessor: "segments",
      Header: "Experiment Segments",
    },
  ];

  return (
    <span>
      <Button
        dataProps={{ "data-qa-selector": "launch-experiment-manager-button" }}
        flavor={Button.flavors.incognito}
        size={Button.sizes.sm}
        onClick={() => setActive(true)}
        ref={triggerRef}
      >
        <span>Experiment Manager</span>
        {Object.keys(experimentOverrides).length > 0 && (
          <Badge text="." flavor={Badge.flavor.dot} />
        )}
      </Button>
      {tableData?.length > 0 && (
        <InfoModal
          {...modalProps}
          title="Experiment override tool"
          __cssFor={modalStyles}
          footer={
            <div css={modalFooterStyles}>
              <Button
                onClick={() => setActive(false)}
                flavor={Button.flavors.secondary}
                __cssFor={modalFooterCancelButtonStyles}
              >
                Close
              </Button>
              <Button
                onClick={() => window.location.reload()}
                flavor={Button.flavors.primary}
                __cssFor={modalFooterUploadButtonStyles}
              >
                Reload the page!
              </Button>
            </div>
          }
        >
          <Table
            __cssFor={tableCss}
            columns={columns}
            data={tableData}
            __renderTitle={() => null}
            description={
              <Button
                onClick={() => {
                  setExperimentOverridesState({});
                  resetExperimentOverrides();
                }}
              >
                Reset User Allocations
              </Button>
            }
          />
        </InfoModal>
      )}
    </span>
  );
};

export default ExperimentManager;
